import React, { useId, useRef } from 'react';
import CustomCheckbox from './CustomCheckbox';

/**
 * DateField
 * ------------------------------------------------------------------
 * Single date input styled to match the design (rounded field with a
 * trailing calendar icon). Wraps a native <input type="date"> so it
 * gets a real OS/browser date picker for free. Clicking anywhere on
 * the field opens the native picker via showPicker() where supported.
 * Empty state hides the browser's mm/dd/yyyy text and shows `label`.
 */
const DateField = ({ label, value, onChange, calendarIconSrc, disabled, id, onBlur, hasError = false, ...rest }) => {
  const autoId = useId();
  const fieldId = id || autoId;
  const inputRef = useRef(null);
  const isEmpty = !value;

  const openPicker = (event) => {
    if (disabled) return;
    event?.preventDefault?.();
    const input = inputRef.current;
    if (!input) return;

    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Fall through when showPicker is blocked by the browser.
      }
    }

    input.focus();
    input.click();
  };

  return (
    <div
      className={`amok-date-field${isEmpty ? ' amok-date-field--empty' : ''}${
        disabled ? ' amok-date-field--disabled' : ''
      }${hasError ? ' amok-date-field--error' : ''}`}
      onClick={openPicker}
    >
      <input
        id={fieldId}
        ref={inputRef}
        type="date"
        className="amok-date-field__input"
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        onClick={(event) => {
          event.stopPropagation();
          openPicker(event);
        }}
        aria-label={label}
        disabled={disabled}
        {...rest}
      />
      <span className="amok-date-field__placeholder" aria-hidden="true">
        {isEmpty ? label : ''}
      </span>
      <button
        type="button"
        className="amok-date-field__icon-btn"
        onClick={(event) => {
          event.stopPropagation();
          openPicker(event);
        }}
        tabIndex={-1}
        aria-hidden="true"
      >
        {calendarIconSrc && <img src={calendarIconSrc} alt="" />}
      </button>
    </div>
  );
};

/**
 * DatePicker
 * ------------------------------------------------------------------
 * Full "Date" field group: Start Date, End Date, and an optional
 * "still ongoing" checkbox that disables the End Date input when
 * checked (e.g. "Still in this role").
 *
 * @param {string} startDate - ISO date string (yyyy-mm-dd)
 * @param {string} endDate - ISO date string (yyyy-mm-dd)
 * @param {function} onStartDateChange
 * @param {function} onEndDateChange
 * @param {boolean} isOngoing
 * @param {function} onOngoingChange
 * @param {string} ongoingLabel - e.g. "Still in this role"
 */
const CustomDatePicker = ({
  label = 'Date',
  startDate = '',
  endDate = '',
  onStartDateChange,
  onEndDateChange,
  isOngoing = false,
  onOngoingChange,
  ongoingLabel = 'Still ongoing',
  hideEndWhenOngoing = false,
  showEndDate: showEndDateProp,
  startPlaceholder = 'Start Date',
  endPlaceholder = 'End Date',
  calendarIconSrc,
  checkIconSrc,
  error,
  onStartBlur,
  onEndBlur,
  disableEndDateUntilStart = false,
  hasError = false,
  startError = false,
  endError = false,
}) => {
  const showEndDate =
    showEndDateProp !== undefined
      ? showEndDateProp
      : !(hideEndWhenOngoing && isOngoing);

  const endDateDisabled = isOngoing || (disableEndDateUntilStart && !startDate);

  return (
    <div className={`amok-date-picker ${!showEndDate ? 'amok-date-picker--ongoing' : ''}`}>
      {label && <span className="amok-date-picker__label">{label}</span>}
      <div className="amok-date-picker__row">
        <DateField
          label={startPlaceholder}
          value={startDate}
          onChange={(e) => onStartDateChange?.(e.target.value)}
          calendarIconSrc={calendarIconSrc}
          onBlur={onStartBlur}
          hasError={startError}
        />
        {showEndDate && (
          <DateField
            label={endPlaceholder}
            value={endDate}
            onChange={(e) => onEndDateChange?.(e.target.value)}
            calendarIconSrc={calendarIconSrc}
            disabled={endDateDisabled}
            onBlur={onEndBlur}
            hasError={endError}
          />
        )}
        {onOngoingChange && (
          <CustomCheckbox
            label={ongoingLabel}
            checked={isOngoing}
            onChange={(e) => onOngoingChange?.(e.target.checked)}
            checkIconSrc={checkIconSrc}
            className="amok-date-picker__ongoing"
          />
        )}
      </div>
      {error && (
        <p className="amok-field__message amok-field__message--error">
          {error}
        </p>
      )}
    </div>
  );
};

export { DateField };
export default CustomDatePicker;